import {Component, inject, OnInit, OnDestroy, ViewChild, ElementRef} from '@angular/core';
import {VehicleProducersService} from '../service/VehicleProducersService';
import {VehicleProducersModel} from '../models/VehicleProducersModel';
import {AuthService} from '../service/auth.service';
import {fadeInAnimation} from '../animations/fadeInAnimation';

@Component({
    selector: 'app-vehicle-producers-table',
    templateUrl: './vehicle-producers-table.component.html',
    styleUrls: ['./vehicle-producers-table.component.css'],
    animations: [fadeInAnimation],
    host: {'[@fadeInAnimation]': ''}
})
export class VehicleProducersTableComponent implements OnInit, OnDestroy {

    duplicateProducerName = false;
    duplicateProducerValue = '';
    draft: VehicleProducersModel = {} as VehicleProducersModel;

    get canSaveNewRow(): boolean {
        const row = this.vehicleProducersService.newInputRow();
        return !!row.correctVehicleProducerName?.trim() && !!row.acceptedVehicleProducerName?.trim() && !this.duplicateProducerName;
    }

    get canSaveDraft(): boolean {
        return !!this.draft.correctVehicleProducerName?.trim() && !!this.draft.acceptedVehicleProducerName?.trim() && !this.duplicateProducerName;
    }

    acceptedNames(names: string | null): string[] {
        return names?.split(',').map(name => name.trim()).filter(Boolean) ?? [];
    }

    public vehicleProducersService = inject(VehicleProducersService);
    @ViewChild('tableContainer', {static: false}) tableContainer!: ElementRef;

    authService = inject(AuthService) as AuthService;

    ngOnInit() {
        this.getVehicleProducers();
    }

    ngOnDestroy() {
        this.vehicleProducersService.resetComponentState();
    }

    getVehicleProducers() {
        this.vehicleProducersService.getVehicleProducersTable();
    }

    // Header search fields (mirrors ECU Builds table UX)
    onSearchCorrectProducer(event: any) {
        const correctTerm = event.target.value ?? '';
        this.vehicleProducersService.applySearchFilters(
            correctTerm,
            this.vehicleProducersService.acceptedSearchTerm()
        );
    }

    onSearchAcceptedProducer(event: any) {
        const acceptedTerm = event.target.value ?? '';
        this.vehicleProducersService.applySearchFilters(
            this.vehicleProducersService.correctSearchTerm(),
            acceptedTerm
        );
    }

    onAddItems() {
        this.vehicleProducersService.openNewRow();

        if (this.tableContainer?.nativeElement) {
            this.tableContainer.nativeElement.scrollTop = 0;
        }
    }

    onNewProducerInput(value: string) {
        if (value.trim().length === 0) {
            this.duplicateProducerName = false;
            this.duplicateProducerValue = '';
            return;
        }

        if (this.vehicleProducersService.checkDuplicateProducer(value)) {
            this.duplicateProducerName = true;
            this.duplicateProducerValue = value;
        } else {
            this.duplicateProducerName = false;
            this.duplicateProducerValue = '';
        }
    }

    onEditProducerInput(value: string, currentId: number) {
        if (value.trim().length === 0) {
            this.duplicateProducerName = false;
            this.duplicateProducerValue = '';
            return;
        }

        const isDuplicate = this.vehicleProducersService
            .getCurrentProducers()
            .some(
                (p) =>
                    p.correctVehicleProducerName?.trim().toLowerCase() === value.trim().toLowerCase() &&
                    p.id !== currentId
            );

        this.duplicateProducerName = isDuplicate;
        this.duplicateProducerValue = isDuplicate ? value : '';
    }


    saveNewRow() {
        this.vehicleProducersService.addVehicleProducer();
    }

    deleteRow() {
        this.vehicleProducersService.editInputRow.set(false);
    }

    onEditItems(index: number) {
        this.vehicleProducersService.editInputRow.set(false);
        this.duplicateProducerName = false;
        this.draft = {...this.vehicleProducersService.getCurrentProducers()[index]};
        this.vehicleProducersService.producerIndex.set(index);
    }

    onSaveItems() {
        if (this.duplicateProducerName) {
            this.vehicleProducersService.notificationService.showErrorMessage(
                'Vehicle producer already exists',
                'Duplicate'
            );
            return;
        }

        if (!this.canSaveDraft) return;
        this.vehicleProducersService.updateVehicleProducer(this.draft);
    }

    onDeleteItems(element: VehicleProducersModel) {
        this.vehicleProducersService.deleteVehicleProducer(element);
    }

    onExitItems() {
        this.vehicleProducersService.producerIndex.set(null);
        this.draft = {} as VehicleProducersModel;
        this.duplicateProducerName = false;
    }

    onScroll() {
        this.vehicleProducersService.onScroll();
    }

    changeSortDirection(column: string) {
        const currentSort = this.vehicleProducersService.sortDirectionValues();
        let newDirection: 'asc' | 'desc' = 'asc';

        if (currentSort.active === column) {
            newDirection = currentSort.direction === 'asc' ? 'desc' : 'asc';
        }

        this.vehicleProducersService.changeSortDirection(
            {active: column, direction: newDirection},
            this.tableContainer?.nativeElement
        );
    }


    onRefreshTable() {
        this.vehicleProducersService.onRefreshTable();

        this.duplicateProducerName = false;
        this.duplicateProducerValue = '';
    }

    hasPermission() {
        return this.authService.isSuperAdmin();
    }
}
